import { Component, type ReactNode } from 'react'
import { useRouteError } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import i18n from '@/lib/i18n'

interface AppErrorBoundaryState {
  hasError: boolean
}

/**
 * Last-resort render boundary. It deliberately never logs props, request
 * bodies or PII; operators rely on the request id and server logs instead.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(): void {
    // Intentionally empty: no user data in client logs.
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <main
        id="main-content"
        role="alert"
        className="grid min-h-dvh place-items-center bg-paper p-6"
      >
        <div className="w-full max-w-xl space-y-4 rounded-card border border-line bg-white p-6 text-center">
          <h1 className="text-3xl">{i18n.t('common.errorTitle')}</h1>
          <p className="text-muted">{i18n.t('errors.unknown')}</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => this.setState({ hasError: false })}
          >
            {i18n.t('common.retry')}
          </button>
        </div>
      </main>
    )
  }
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const { t } = useTranslation()
  const key =
    error instanceof Error ? friendlyErrorKey(error) : 'errors.unknown'

  return (
    <main
      id="main-content"
      role="alert"
      className="grid min-h-dvh place-items-center bg-paper p-6"
    >
      <div className="w-full max-w-xl space-y-4 rounded-card border border-line bg-white p-6 text-center">
        <h1 className="text-3xl">{t('common.errorTitle')}</h1>
        <p className="text-muted">{t(key)}</p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => window.location.reload()}
        >
          {t('common.retry')}
        </button>
      </div>
    </main>
  )
}
